/**
 * Creates a debounced function that delays invoking the provided function until
 * after `wait` milliseconds have elapsed since the last time it was invoked.
 *
 * @param fn - The function to debounce
 * @param wait - The number of milliseconds to delay (default: 1500)
 * @returns An object with run, cancel, and flush methods
 */
export const debounce = <T extends (...args: any[]) => any>(
  fn: T,
  wait = 1500
) => {
  let timeoutId: NodeJS.Timeout | null = null

  const run = (...args: Parameters<T>) => {
    if (timeoutId) {
      clearTimeout(timeoutId)
    }
    timeoutId = setTimeout(() => {
      fn(...args)
      timeoutId = null
    }, wait)
  }

  const cancel = () => {
    if (timeoutId) {
      clearTimeout(timeoutId)
      timeoutId = null
    }
  }

  const flush = (...args: Parameters<T>) => {
    if (timeoutId) {
      clearTimeout(timeoutId)
      timeoutId = null
    }
    fn(...args)
  }

  return { run, cancel, flush }
}

export default debounce
